/*
 * Gráficas del tablero de Marcaciones. Es el ÚNICO archivo que importa la librería de gráficas
 * (react-apexcharts + apexcharts, dependencias nuevas de la etapa 4): si el revisor la rechaza, el plan B
 * reemplaza solo este archivo por uno con react-google-charts (plan de la etapa 4, sección "Plan B") y
 * las secciones no cambian.
 *
 * Exporta Sparkline (minigráfica de los KPI), BarChart (barras verticales u horizontales), HeatmapChart
 * (mapa de calor) y TrendChart (líneas en %). La librería se carga DIFERIDA (React.lazy): queda en un
 * archivo aparte que solo se descarga cuando se dibuja la primera gráfica, así no pesa en el resto del
 * sistema (sumaba ~250 kB comprimidos al bundle principal). Todas: sin barra de herramientas ni zoom, sin animación si
 * el sistema pide movimiento reducido, colores de los tokens de attendance.css, textos en español y un
 * contenedor role="img" con aria-label que resume los datos (una gráfica sola no se lee con lector de
 * pantalla). RESUMEN ACCESIBLE: todas reciben la prop `summary` (texto con los valores, p. ej. armado con
 * heatmapLabel/executiveSummary de utils/dashboard.js); se usa como aria-label y como texto oculto
 * (.mc-sr-only). Las secciones (Tasks 3-5) DEBEN pasarla. Mientras baja el chunk se muestra
 * "Cargando gráfica…" (role=status). Los datos llegan ya preparados por utils/dashboard.js; con datos vacíos o null no se rompen
 * (arreglos por defecto). El sistema no tiene modo oscuro: los colores están pensados para fondo claro.
 */
import React, {Suspense, lazy} from "react";
import {formatNumber} from "../utils/rows";
import {escapeHtml} from "../utils/dashboard";

export const CHART_COLORS = {
    ink: '#1f2a3c',
    muted: '#5b6576',
    line: '#e3e8f0',
    work: '#5a8dee',
    up: '#39b27a',
    down: '#f28b76',
    gold: '#b8860b',
    neutral: '#5a8dee',
    none: '#9aa3b2',
};

function animationsEnabled() {
    return !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function chartBase(extra = {}) {
    return {
        toolbar: {show: false},
        zoom: {enabled: false},
        animations: {enabled: animationsEnabled()},
        fontFamily: '"IBM Plex Sans", sans-serif',
        foreColor: CHART_COLORS.muted,
        parentHeightOffset: 0,
        ...extra,
    };
}

const GRID = {borderColor: CHART_COLORS.line, strokeDashArray: 3};

/*
 * Formateadores a nivel de módulo (identidad estable): react-apexcharts compara las opciones con deepEqual y una
 * función nueva en cada render cuenta como cambio y dispara updateOptions (redibujado) sin motivo.
 */
const barTitle = (value, opts) => escapeHtml(opts && opts.w ? opts.w.globals.labels[opts.dataPointIndex] : value);
const noTitle = () => '';
const barValueFormatters = {};
/** Formateador del valor de barras por unidad (una sola función por unidad, para no cambiar entre renders). */
const barValue = (unit) => {
    if (!barValueFormatters[unit]) {
        barValueFormatters[unit] = (value) => `${formatNumber(value, unit === ' h' ? 1 : 0)}${unit}`;
    }
    return barValueFormatters[unit];
};
const heatHourLabel = (value) => (value && value.endsWith(':00') ? value : '');
// Título del tooltip del mapa: siempre la hora del bloque (el eje solo rotula las horas en punto).
const heatTooltipTitle = (value, opts) => {
    const block = opts && opts.w ? opts.w.globals.labels[opts.dataPointIndex] : undefined;
    return escapeHtml(block !== undefined ? block : value);
};
const heatSeriesName = (name) => escapeHtml(name);
const heatTooltipValue = (value, {dataPointIndex, w}) => {
    const block = w.globals.labels[dataPointIndex] || '';
    return `${value} ${value === 1 ? 'entrada' : 'entradas'} desde las ${block}`;
};
const percentAxis = (value) => `${Math.round(value)} %`;
const percentTooltip = (value) => (value === null || value === undefined ? '—' : `${formatNumber(value, 1)} %`);

const ReactApexChart = lazy(() => import("react-apexcharts"));

/** Contenedor accesible de una gráfica; reserva el alto mientras se descarga la librería. */
const ChartFrame = ({label, summary, height, className = 'mc-chart', children}) => (
    <div className={className} role="img" aria-label={summary || label} style={{minHeight: height}}>
        {summary ? <span className="mc-sr-only">{summary}</span> : null}
        <Suspense fallback={<div role="status" className="mc-chart-loading" style={{minHeight: height}}>Cargando gráfica…</div>}>{children}</Suspense>
    </div>
);

/** Minigráfica de un KPI (valores de los días con dato, en orden). Con menos de 2 puntos no se dibuja. */
export const Sparkline = ({values = [], color, label, summary}) => {
    if (!values || values.length < 2) return <div className="mc-chart mc-chart--spark" aria-hidden="true"/>;
    return (
        <ChartFrame className="mc-chart mc-chart--spark" label={label} summary={summary} height={44}>
            <ReactApexChart
                type="line"
                height={44}
                series={[{name: label, data: values}]}
                options={{
                    chart: chartBase({sparkline: {enabled: true}}),
                    stroke: {curve: 'smooth', width: 2},
                    colors: [color],
                    markers: {size: 0},
                    tooltip: {enabled: false},
                }}
            />
        </ChartFrame>
    );
};

/** Barras: `colors` (uno por barra) opcional; `horizontal` para rankings largos (nombres a la izquierda). */
export const BarChart = ({categories = [], values = [], colors, horizontal = false, height = 260, label, summary, unit = ''}) => (
    <ChartFrame label={label} summary={summary} height={height}>
        <ReactApexChart
            type="bar"
            height={height}
            series={[{name: label, data: values || []}]}
            options={{
                chart: chartBase(),
                plotOptions: {bar: {horizontal, distributed: Boolean(colors), borderRadius: 4, barHeight: '70%', columnWidth: '55%'}},
                colors: colors || [CHART_COLORS.work],
                dataLabels: {enabled: false},
                legend: {show: false},
                grid: GRID,
                xaxis: {categories: categories || [], labels: {trim: true, hideOverlappingLabels: true}},
                yaxis: {labels: {maxWidth: horizontal ? 160 : 60}},
                // ApexCharts arma el tooltip con innerHTML: el nombre (categoría) viene del API y se escapa.
                tooltip: {
                    x: {formatter: barTitle},
                    y: {formatter: barValue(unit), title: {formatter: noTitle}},
                },
            }}
        />
    </ChartFrame>
);

/** Mapa de calor: `series` y `ranges` de utils/dashboard.js (heatmapSeries, heatmapRanges). */
export const HeatmapChart = ({series = [], ranges = [], label, summary}) => {
    const height = Math.max(160, 44 * (series || []).length + 60);
    return (
        <ChartFrame label={label} summary={summary} height={height}>
            <ReactApexChart
                type="heatmap"
                height={height}
                series={series || []}
                options={{
                    chart: chartBase(),
                    dataLabels: {enabled: false},
                    legend: {show: false},
                    stroke: {width: 2, colors: ['#ffffff']},
                    plotOptions: {heatmap: {radius: 3, enableShades: false, colorScale: {ranges: ranges || []}}},
                    xaxis: {
                        labels: {rotate: 0, formatter: heatHourLabel},
                        tooltip: {enabled: false},
                    },
                    tooltip: {
                        x: {show: true, formatter: heatTooltipTitle},
                        y: {title: {formatter: heatSeriesName}, formatter: heatTooltipValue},
                    },
                }}
            />
        </ChartFrame>
    );
};

/** Líneas en porcentaje (0-100) con leyenda: tendencia semanal. */
export const TrendChart = ({categories = [], series = [], label, summary, height = 260}) => (
    <ChartFrame label={label} summary={summary} height={height}>
        <ReactApexChart
            type="line"
            height={height}
            series={series || []}
            options={{
                chart: chartBase(),
                colors: [CHART_COLORS.work, CHART_COLORS.gold, CHART_COLORS.up],
                stroke: {curve: 'smooth', width: 3},
                markers: {size: 4},
                dataLabels: {enabled: false},
                grid: GRID,
                legend: {show: true, position: 'bottom'},
                xaxis: {categories: categories || []},
                yaxis: {min: 0, max: 100, tickAmount: 4, labels: {formatter: percentAxis}},
                tooltip: {y: {formatter: percentTooltip}},
            }}
        />
    </ChartFrame>
);
